import { Language } from '@/utils/translations/types/TypeLanguagesAndLocales';
import {
  TypeUserPreferenceName,
  TypeUserPreferenceRecord,
  TypeUserRecord,
  UserPreference,
} from '@/lib/api/accountManager/types';
import { EditCommunicationPreferencesFormState, PreferencesMap } from './types';
import { LanguageTranslationKeyMap } from '@/components/common/EditCommunicationPreferences/constants';

/**
 * Safely converts a string to Language enum value
 * Returns null if the string is not a valid Language value
 */
export function stringToLanguage(
  language: string | null | undefined,
): Language | null {
  if (!language) return null;

  const validLanguages = Object.values(Language);

  return validLanguages.includes(language as Language)
    ? (language as Language)
    : null;
}

export function buildDefaultFormValues(
  user: TypeUserRecord,
): EditCommunicationPreferencesFormState {
  // data integrity fallback:  in case the user record doesn't have the preferences saved yet, the form should still support them
  const defaultPreferences = {
    [UserPreference.EmailMarketing]: {
      preference_name: UserPreference.EmailMarketing,
      preference_value: false,
    },
    [UserPreference.SmsTransactional]: {
      preference_name: UserPreference.SmsTransactional,
      preference_value: false,
    },
    [UserPreference.SmsMarketing]: {
      preference_name: UserPreference.SmsMarketing,
      preference_value: false,
    },
  } as PreferencesMap;

  const savedUserPreferences = user.user_preferences.reduce(
    (acc, preference) => {
      acc[preference.preference_name] = {
        preference_name: preference.preference_name,
        preference_value: preference.preference_value,
      };

      return acc;
    },
    {} as PreferencesMap,
  );

  return {
    uuid: user.uuid,
    phoneNumber: user.phone_number || '',
    language: stringToLanguage(user.language),
    preferences: { ...defaultPreferences, ...savedUserPreferences },
  };
}

export function getPreferencesByType(
  preferences: PreferencesMap,
  preferenceTypes: TypeUserPreferenceName[],
): TypeUserPreferenceRecord[] {
  return preferenceTypes
    .map((prefName) => preferences[prefName])
    .filter((pref): pref is TypeUserPreferenceRecord => pref !== undefined);
}

export function getLabelForLanguage(
  language: Language | null,
  translations: Record<string, string>,
) {
  const labelMapLookup = language && LanguageTranslationKeyMap[language];
  const label = labelMapLookup && translations[labelMapLookup];

  return label || language || '-';
}
